import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { toast } from "sonner";
import AppraisalForm, { emptyAppraisal, appraisalToBody } from "@/dealership/components/AppraisalForm";
import DeskDealButton from "@/dealership/components/desk/DeskDealButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, Camera, Sparkles, Search, X, ExternalLink,
  Loader2, AlertTriangle, BadgeDollarSign, Car, Wrench, Search as SearchIcon,
} from "lucide-react";

const gradeStyle = {
  excellent: "bg-green-500/12 text-green-400",
  good: "bg-blue-500/12 text-blue-400",
  fair: "bg-amber-500/12 text-amber-400",
  poor: "bg-red-500/12 text-red-400",
};

async function resizeToBase64(file, maxDim = 1280, quality = 0.8) {
  const url = URL.createObjectURL(file);
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = url;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  return canvas.toDataURL("image/jpeg", quality).split(",")[1];
}

function ValueCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className={`p-5 ${accent ? "border-primary/40 bg-primary/5" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="font-heading text-2xl font-extrabold mt-3 font-mono-data">{value}</p>
    </Card>
  );
}

export default function AppraisalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyAppraisal);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: a, isLoading } = useQuery({
    queryKey: ["appraisal", id],
    queryFn: async () => (await api.get(`/appraisals/${id}`)).data,
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.photo_analysis_status === "running" || d.appraise_status === "running") ? 3000 : false;
    },
  });

  const { data: photos = [] } = useQuery({
    queryKey: ["appraisal-photos", id],
    queryFn: async () => (await api.get(`/appraisals/${id}/photos`)).data,
  });

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/appraisals/${id}`, appraisalToBody(form))).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appraisal", id] });
      qc.invalidateQueries({ queryKey: ["appraisals"] });
      toast.success("Appraisal updated");
      setEditOpen(false);
    },
    onError: () => toast.error("Failed to update"),
  });

  const analyzeMut = useMutation({
    mutationFn: async () => (await api.post(`/appraisals/${id}/analyze-photos`)).data,
    onSuccess: () => {
      toast.success("AI photo inspection started");
      qc.invalidateQueries({ queryKey: ["appraisal", id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start analysis"),
  });

  const appraiseMut = useMutation({
    mutationFn: async () => (await api.post(`/appraisals/${id}/appraise`)).data,
    onSuccess: () => {
      toast.success("Market appraisal started — this takes about a minute");
      qc.invalidateQueries({ queryKey: ["appraisal", id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start appraisal"),
  });

  const delPhotoMut = useMutation({
    mutationFn: async (photoId) => api.delete(`/appraisals/${id}/photos/${photoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["appraisal-photos", id] }),
  });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 20);
    if (!files.length) return;
    setUploading(true);
    try {
      const payload = [];
      for (const f of files) payload.push({ data: await resizeToBase64(f), content_type: "image/jpeg" });
      await api.post(`/appraisals/${id}/photos`, { photos: payload });
      toast.success(`${payload.length} photo(s) uploaded`);
      qc.invalidateQueries({ queryKey: ["appraisal-photos", id] });
      qc.invalidateQueries({ queryKey: ["appraisal", id] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (isLoading || !a) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const ai = a.ai_condition;
  const ap = a.appraisal;
  const analyzing = a.photo_analysis_status === "running";
  const appraising = a.appraise_status === "running";

  return (
    <div className="p-8 max-w-6xl">
      <button onClick={() => navigate("/dealership/appraisals")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="appraisal-back-button">
        <ArrowLeft className="h-4 w-4" /> All appraisals
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold" data-testid="appraisal-title">
            {a.year} {a.make} {a.model} {a.trim}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {a.client_name} {a.client_phone && `· ${a.client_phone}`} · {Number(a.odometer).toLocaleString()} km
            {a.vin && <span className="font-mono-data"> · VIN {a.vin}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <DeskDealButton appraisalId={id} label="Desk with this trade" testid="appraisal-desk-button" />
          <Button variant="outline" onClick={() => { setForm({ ...emptyAppraisal, ...Object.fromEntries(Object.keys(emptyAppraisal).map((k) => [k, a[k] ?? ""])) }); setEditOpen(true); }} data-testid="edit-appraisal-button">
            <Pencil className="h-4 w-4 mr-2" /> Edit Details
          </Button>
          <Button onClick={() => appraiseMut.mutate()} disabled={appraising || appraiseMut.isPending} data-testid="run-appraisal-button">
            {appraising ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            {appraising ? "Scanning Ontario market…" : ap ? "Re-run Appraisal" : "Run Market Appraisal"}
          </Button>
        </div>
      </div>

      {a.error_message && (a.photo_analysis_status === "error" || a.appraise_status === "error") && (
        <Card className="p-4 mb-4 border-red-500/35 bg-red-500/[0.07]" data-testid="appraisal-error-banner">
          <p className="text-sm text-red-300"><AlertTriangle className="h-4 w-4 inline mr-2" />{a.error_message} — try again.</p>
        </Card>
      )}

      {/* Valuation results */}
      {ap && (
        <div className="mb-6" data-testid="appraisal-results">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <ValueCard icon={BadgeDollarSign} label="Trade-In Offer Range" value={`${money(ap.trade_in_low)} – ${money(ap.trade_in_high)}`} accent testid="appraisal-tradein-card" />
            <ValueCard icon={Car} label="Dealer Retail Value" value={money(ap.retail_value)} testid="appraisal-retail-card" />
            <ValueCard icon={BadgeDollarSign} label="Private Party Value" value={money(ap.private_party_value)} testid="appraisal-private-card" />
          </div>
          <Card className="p-5">
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm mb-3">
              <span>Market avg: <b className="font-mono-data">{money(ap.market_avg)}</b></span>
              <span>Range: <b className="font-mono-data">{money(ap.market_low)} – {money(ap.market_high)}</b> ({ap.sample_size} comps)</span>
              <span>Condition adj: <b className={`font-mono-data ${ap.condition_adjustment < 0 ? "text-red-400" : "text-green-400"}`}>{money(ap.condition_adjustment)}</b></span>
              <span>Recon estimate: <b className="font-mono-data">{money(ap.recon_estimate)}</b></span>
              <span>Confidence: <b className="capitalize">{ap.confidence}</b></span>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="appraisal-reasoning">{ap.reasoning}</p>
            {(ap.comps || []).length > 0 && (
              <div className="mt-4 grid gap-1.5">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Ontario comps used</p>
                {ap.comps.map((c, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm bg-secondary/50 rounded-md px-3 py-2">
                    <span className="font-medium flex-1 min-w-[200px]">{c.title}</span>
                    <span className="font-mono-data">{c.price ? money(c.price) : "—"}</span>
                    <span className="text-muted-foreground font-mono-data">{c.km ? `${Number(c.km).toLocaleString()} km` : ""}</span>
                    <span className="text-xs text-muted-foreground">{c.location} · {c.source}</span>
                    {c.url ? (
                      <a href={c.url} target="_blank" rel="noreferrer" className="text-primary" title="Open exact listing"><ExternalLink className="h-3.5 w-3.5" /></a>
                    ) : (
                      <a
                        href={`https://www.google.com/search?q=${encodeURIComponent(`${c.title || ""} for sale Ontario ${c.source || ""}`)}`}
                        target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"
                        title="Exact link unavailable — search for this listing"
                      >
                        <SearchIcon className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Photos */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold flex items-center gap-2"><Camera className="h-4 w-4" /> Photos ({photos.length}/20)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photos-button">
                {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Upload
              </Button>
              <Button size="sm" onClick={() => analyzeMut.mutate()} disabled={analyzing || photos.length === 0 || analyzeMut.isPending} data-testid="analyze-photos-button">
                {analyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {analyzing ? "Inspecting…" : "AI Condition Scan"}
              </Button>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="photo-file-input" />
          {photos.length === 0 ? (
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-border rounded-lg py-10 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors" data-testid="photo-dropzone">
              Upload exterior, interior, wheel and damage photos — the AI will grade condition and estimate recon costs.
            </button>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative group aspect-[4/3] rounded-md overflow-hidden border border-border">
                  <img src={`data:${p.content_type};base64,${p.data}`} alt="vehicle" className="w-full h-full object-cover" />
                  <button onClick={() => delPhotoMut.mutate(p.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${p.id}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* AI condition report */}
        <Card className="p-5">
          <p className="font-semibold flex items-center gap-2 mb-4"><Sparkles className="h-4 w-4" /> AI Condition Report</p>
          {!ai ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {analyzing ? "Inspecting photos…" : "Upload photos and run the AI Condition Scan to grade this vehicle."}
            </p>
          ) : (
            <div className="space-y-4" data-testid="ai-condition-report">
              <div className="flex flex-wrap gap-2">
                <Badge className={`${gradeStyle[ai.overall_grade] || "bg-zinc-500/12 text-zinc-400"} border-0 capitalize`} data-testid="ai-overall-grade">Overall: {ai.overall_grade}</Badge>
                {ai.exterior?.grade && <Badge className={`${gradeStyle[ai.exterior.grade] || ""} border-0 capitalize`}>Exterior: {ai.exterior.grade}</Badge>}
                {ai.interior?.grade && <Badge className={`${gradeStyle[ai.interior.grade] || ""} border-0 capitalize`}>Interior: {ai.interior.grade}</Badge>}
              </div>
              <p className="text-sm">{ai.summary}</p>
              {(ai.damage_detected || []).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Damage detected</p>
                  <ul className="text-sm space-y-1">{ai.damage_detected.map((d, i) => <li key={i} className="flex gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />{d}</li>)}</ul>
                </div>
              )}
              {(ai.recon_items || []).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Estimated recon</p>
                  <div className="space-y-1">
                    {ai.recon_items.map((r, i) => (
                      <div key={i} className="flex justify-between text-sm bg-secondary/50 rounded px-2.5 py-1.5">
                        <span className="flex items-center gap-2"><Wrench className="h-3.5 w-3.5 text-muted-foreground" />{r.description}</span>
                        <span className="font-mono-data">{money(r.estimated_cost)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-semibold px-2.5 pt-1">
                      <span>Total recon</span>
                      <span className="font-mono-data">{money((ai.recon_items || []).reduce((s, r) => s + Number(r.estimated_cost || 0), 0))}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Declared condition summary */}
      <Card className="p-5 mt-6">
        <p className="font-semibold mb-3">Declared Condition & History</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <div><p className="text-xs text-muted-foreground">Accidents</p><p className="capitalize">{a.accidents}{a.accident_details ? ` — ${a.accident_details}` : ""}</p></div>
          <div><p className="text-xs text-muted-foreground">Owners</p><p>{a.owners ?? "Unknown"}</p></div>
          <div><p className="text-xs text-muted-foreground">Service history</p><p className="capitalize">{a.service_history}</p></div>
          <div><p className="text-xs text-muted-foreground">Tires / Brakes</p><p>{a.tires_pct != null ? `${a.tires_pct}%` : "—"} / {a.brakes_pct != null ? `${a.brakes_pct}%` : "—"}</p></div>
          {a.options && <div className="col-span-2"><p className="text-xs text-muted-foreground">Options</p><p>{a.options}</p></div>}
          {a.mechanical_notes && <div className="col-span-2"><p className="text-xs text-muted-foreground">Mechanical</p><p>{a.mechanical_notes}</p></div>}
          {a.exterior_notes && <div className="col-span-2"><p className="text-xs text-muted-foreground">Exterior</p><p>{a.exterior_notes}</p></div>}
          {a.interior_notes && <div className="col-span-2"><p className="text-xs text-muted-foreground">Interior</p><p>{a.interior_notes}</p></div>}
          {a.notes && <div className="col-span-2"><p className="text-xs text-muted-foreground">Notes</p><p>{a.notes}</p></div>}
        </div>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Appraisal</DialogTitle></DialogHeader>
          <AppraisalForm form={form} set={set} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.client_name || !form.year || !form.make || !form.model || saveMut.isPending} data-testid="update-appraisal-button">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
